

















import Vue from 'vue';
import LoadingPage from '@/components/LoadingPage.vue';
import ProuniEnrollmentLinkGate from '@/components/prouni/ProuniEnrollmentLinkGate.vue';
import Analytics from '@/helpers/analytics';
import prouniAuth from '@/helpers/prouniAuth';
import { getProuniVoucherFromRoute, isProuniVoucherRefusal } from '@/helpers/prouniVoucher';
import { getPartnerCouponFromCookie } from '@/helpers/partnerCoupon';
import SentryClient from '@/helpers/sentry';
import viewsNames from './router/viewsNames';
import { Icon, Tooltip } from '@/components/ds';
import {
  showEqualWebDrawer,
  installEqualWebLaunchButtonOffscreenStyle,
} from './helpers/accessibility';
import { initVlibras } from './helpers/vlibras';
import { startTranslationDetection } from './helpers/translationDetection';

const equalWebSiteKey = 'aecf6ffe12fa1c1d26fe0ed3b1d39c4a';
const equalWebIntegrityKey =
  'sha512-3qLj5jbjMQnXk+FqEdVJjUnjJBGuBTRVOwaiT0ms6mQKQcrz4nulBxl2Hsr0/PpvEqdyJsMsU1NB+Mtfzw8hxA==';

type ProuniLinkGateMode = '' | 'login' | 'error';

// Resultado da entrada por `?cupom=`: `notProuni` é a recusa do backend que indica
// cupom de campanha (PRD-3095) — o bootstrap segue como matrícula comum.
type ProuniLinkOutcome = 'authenticated' | 'notProuni' | 'blocked';

// Seletor do container do iframe de login dentro do ProuniEnrollmentLinkGate.
const PROUNI_LINK_IFRAME_SELECTOR = '.prouni-link-gate__iframe';

export default Vue.extend({
  components: { LoadingPage, Icon, Tooltip, ProuniEnrollmentLinkGate },
  data() {
    return {
      isLoading: false,
      prouniLinkGateMode: '' as ProuniLinkGateMode,
      prouniLoggedEmail: '',
      prouniVoucher: '',
    };
  },
  computed: {
    isLoadingApp(): boolean {
      return this.$store.getters.isLoadingApp;
    },
  },
  async mounted() {
    // antes de qualquer early return: tradutor ativo é justamente o cenário em que o
    // funil quebra, e queremos o dado inclusive nas rotas que saem cedo daqui
    startTranslationDetection();

    const isLoginValidation = this.$router.currentRoute.name === viewsNames.VALIDATE_LOGIN;
    if (isLoginValidation) return;

    // ProUni is an independent flow with its own pipeline and pocket-only auth.
    // It must not trigger the enrollment bootstrap (which requires an admission).
    const isProuniRoute = (this.$router.currentRoute.path || '').startsWith('/prouni');
    if (isProuniRoute) return;

    await this.bootstrapEnrollment();
  },
  methods: {
    // Corpo do bootstrap, separado do hook para poder ser reexecutado depois do
    // login por iframe do ProUni (o onSuccess do pocket chega bem depois do mount).
    async bootstrapEnrollment(): Promise<void> {
      // Entrada pelo link do ProUni (`/?cupom=<código>`). Sem o param, nada
      // aqui roda e a matrícula do processo seletivo comum segue idêntica.
      const prouniVoucher = getProuniVoucherFromRoute(this.$router.currentRoute);
      this.prouniVoucher = prouniVoucher || '';

      let campaignCoupon: string | undefined;

      if (prouniVoucher) {
        const outcome = await this.startProuniEnrollmentLink(prouniVoucher);
        // Sem token de admissão não há bootstrap: a tela do gate (login ou recusa)
        // fica no ar em vez de redirecionar o aluno para o processo seletivo.
        if (outcome === 'blocked') return;
        if (outcome === 'notProuni') campaignCoupon = prouniVoucher;
      }

      try {
        this.isLoading = true;
        const hasEnrollment = await this.$store.dispatch('GET_ENROLLMENT');

        if (prouniVoucher && !campaignCoupon) {
          // Grava o cupom pessoal na matrícula. A aplicação em si já existe e roda
          // sozinha em AcademicFormation.vue — aqui só alimentamos o partnerCode.
          await this.$store.dispatch('UPSERT_ENROLLMENT', { partnerCode: prouniVoucher });

          // Só DEPOIS de o cupom estar persistido: o campo em Dados Pessoais fica
          // travado porque trocá-lo desfaz a bolsa, e não há caminho de reaplicar
          // cupom depois que a matrícula é criada no ERP.
          this.$store.commit('SET_PARTNER_CODE_LOCKED', true);
        } else if (campaignCoupon) {
          await this.applyCampaignCoupon(campaignCoupon, hasEnrollment);
        } else {
          await this.applyPartnerCoupon(hasEnrollment);
        }

        await this.$store.dispatch('PULL_PIPELINE');
        await this.$store.dispatch('START_PIPELINE');

        const { email, fullName: name } = this.$store.getters.personalData || {};
        Analytics.identify({ id: email, email, name });
      } catch (err) {
        // O aluno de ProUni entrou por link, não pelo processo seletivo: mandá-lo
        // para lá seria um beco sem saída. Vale também para o cupom recusado: sem
        // admissions-auth-token, pode ser o aprovado logado com a conta errada.
        if (prouniVoucher) {
          this.showProuniLinkError();
          return;
        }

        const admissionsUrl: string = process.env.VUE_APP_ADMISSIONS_URL || '/';

        window.location.replace(admissionsUrl);
      } finally {
        this.isLoading = false;
      }

      this.insertEqualWebScript();
    },
    /**
     * Cupom do parceiro (unico): vem no JWT do cookie `d-partner`, não na URL.
     * Grava na matrícula e trava o campo em Dados Pessoais.
     *
     * Roda com `isLoading` ainda `true` de propósito: é o remount do `router-view`
     * que faz o form ler o `partnerCode` novo — prop que muda depois não reflete
     * no campo, porque `PersonalDataForm` copia as props no `data()`.
     */
    async applyPartnerCoupon(hasEnrollment: boolean): Promise<void> {
      const coupon = await getPartnerCouponFromCookie();
      const {
        partnerCode: currentCode,
        isProuniEnrollment,
        admissionEvaluationMethod,
      } = this.$store.getters;

      // Bolsa do ProUni vale mais que campanha de parceiro: quem já é ProUni e
      // volta sem o `?cupom=` (refresh, bookmark) não pode ter o cupom trocado.
      // O sinal é o tipo da admissão, não o texto do cupom (o código do JACAD não
      // tem prefixo). Se a admissão não carregou, não dá para descartar ProUni:
      // aí um cupom já gravado e diferente fica como está.
      const isAdmissionTypeUnknown = admissionEvaluationMethod === undefined;
      const mayOverwriteProuni = isAdmissionTypeUnknown && !!currentCode && currentCode !== coupon;

      if (!coupon || isProuniEnrollment || mayOverwriteProuni) {
        if (!hasEnrollment) await this.$store.dispatch('UPSERT_ENROLLMENT', {});
        return;
      }

      try {
        // Sessão retomada com o cupom já gravado não repete a mutation.
        if (!hasEnrollment || currentCode !== coupon) {
          await this.$store.dispatch('UPSERT_ENROLLMENT', { partnerCode: coupon });
        }

        // Só depois de persistido, como no ProUni.
        this.$store.commit('SET_PARTNER_CODE_LOCKED', true);
      } catch (err) {
        // Diferente do link do ProUni: este aluno veio pelo processo seletivo e
        // tem matrícula válida. Derrubar o funil por causa de um cupom seria
        // conversão perdida — segue destravado, dá para digitar à mão.
        SentryClient.captureError(err as Error);
      }
    },
    /**
     * Cupom de campanha que chegou por `?cupom=` (PRD-3095). Roda o caminho comum
     * do cookie `d-partner` e só depois pré-preenche o cupom da URL, **sem travar**:
     * não há como saber pelo texto que é campanha, e quem valida é o
     * `APPLY_VOUCHER`. Cupom já gravado (do parceiro, digitado ou o do ProUni, que
     * não tem prefixo) não é sobrescrito.
     */
    async applyCampaignCoupon(coupon: string, hasEnrollment: boolean): Promise<void> {
      // Sem matrícula, o upsert vazio do applyPartnerCoupon seria seguido de outro:
      // cria direto com o cupom quando não há cookie de parceiro.
      const partnerCoupon = hasEnrollment ? undefined : await getPartnerCouponFromCookie();
      if (hasEnrollment || partnerCoupon) await this.applyPartnerCoupon(hasEnrollment);

      if (this.$store.getters.isPartnerCodeLocked || this.$store.getters.partnerCode) return;

      try {
        await this.$store.dispatch('UPSERT_ENROLLMENT', { partnerCode: coupon });
      } catch (err) {
        // Como no cupom do parceiro: o aluno pode digitar à mão em Dados Pessoais.
        SentryClient.captureError(err as Error);
        if (!hasEnrollment) await this.$store.dispatch('UPSERT_ENROLLMENT', {});
      }
    },
    /**
     * Garante a sessão Pocket e troca o cupom pelo `admissions-auth-token`.
     * `notProuni` quando o backend recusa o cupom como ProUni desta conta.
     */
    async startProuniEnrollmentLink(voucher: string): Promise<ProuniLinkOutcome> {
      if (!prouniAuth.isLogged()) {
        // Reusa o login por iframe já escrito para o ProUni. O onSuccess retoma
        // o bootstrap inteiro, então esta chamada não continua o fluxo.
        this.prouniLinkGateMode = 'login';
        await this.$nextTick();

        prouniAuth.loginWithIframe(
          {
            onSuccess: () => {
              this.prouniLinkGateMode = '';
              this.bootstrapEnrollment();
            },
            onError: () => {
              this.showProuniLinkError();
            },
          },
          PROUNI_LINK_IFRAME_SELECTOR,
          'prouni-iframe',
        );

        return 'blocked';
      }

      try {
        this.isLoading = true;
        await this.$store.dispatch('EXCHANGE_PROUNI_ENROLLMENT_TOKEN', voucher);
        // O backend só troca o token de quem tem inscrição ProUni aprovada com este
        // cupom: a partir daqui a matrícula é ProUni, antes mesmo da admissão carregar.
        this.$store.commit('SET_PROUNI_ENROLLMENT_LINK', true);
        this.prouniLinkGateMode = '';
        return 'authenticated';
      } catch (err) {
        if (isProuniVoucherRefusal(err)) return 'notProuni';

        this.showProuniLinkError();
        return 'blocked';
      } finally {
        this.isLoading = false;
      }
    },
    showProuniLinkError(): void {
      this.prouniLoggedEmail = prouniAuth.getPocketProfile()?.email || '';
      this.prouniLinkGateMode = 'error';
      this.isLoading = false;
    },
    insertEqualWebScript(): void {
      if ((window as any).descomplicaEqualWebInjected) {
        return;
      }
      (window as any).descomplicaEqualWebInjected = true;

      (window as any).interdeal = {
        sitekey: equalWebSiteKey,
        hideBtn: true,
        Position: 'Right',
        Menulang: 'PT',
        domains: {
          js: 'https://cdn.equalweb.com/',
          acc: 'https://access.equalweb.com/',
        },
        btnStyle: {
          vPosition: [null, '20%'],
          color: {
            main: '#2E850F',
            second: '#000000',
          },
          scale: [0.5],
          icon: {
            type: 1,
            shape: 'circle',
            outline: false,
          },
        },
      };

      // Accessibility Utilitary
      const scriptElement = document.createElement('script');
      scriptElement.src = 'https://cdn.equalweb.com/core/5.3.1/accessibility.js';
      scriptElement.defer = true;
      scriptElement.integrity = equalWebIntegrityKey;
      scriptElement.crossOrigin = 'anonymous';
      scriptElement.setAttribute('data-cfasync', 'true');
      scriptElement.onload = () => {
        installEqualWebLaunchButtonOffscreenStyle();
        initVlibras();
      };

      document.body.appendChild(scriptElement);

      // Botmaker
      if (process.env.VUE_APP_BOTMAKER_URL) {
        const match = document.cookie.match(RegExp('(?:^|;\\s*)d-profile=([^;]*)'));
        const dProfileCookie = match ? match[1] : '';

        window.BOTMAKER_VAR = {
          cookie_accounts: decodeURIComponent(dProfileCookie),
        };

        const botmaker = document.createElement('script');
        botmaker.type = 'text/javascript';
        botmaker.async = true;
        botmaker.src = process.env.VUE_APP_BOTMAKER_URL;
        document.body.appendChild(botmaker);
      }
    },
    showAccessibilityDrawer() {
      Analytics.track('Open accessibility drawer', {
        origin: 'Botão Acessibilidade',
      });

      showEqualWebDrawer();
    },
  },
});
